import { useEffect, useMemo, useRef, useState } from "react";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import AutoGrowTextarea from "@/shared/components/AutoGrowTextarea";
import { encodeArtwork } from "@/shared/utils/artworkTransport";
import MailPiecePreview from "./MailPiecePreview";
import {
    CANVAS, MAIL_PIECE_TEMPLATES, templateById, type MailPieceSize,
    clampQrPlacement, QR_DEFAULT, type QrPlacement,
} from "../mailPieceTemplates";
import {
    collectFontFaces, compositionToPostcardHtml, type ConversionWarning,
} from "../kinemaToPostcard";
import { imageCardHtml } from "../mailPieceTemplates";
import { MediaService } from "@services/media/MediaService";
import { FontService } from "@services/font/FontService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { ApprovedUrl } from "@models/approvedUrl.model";
import { qrDestinationProblem } from "../qrDestination";
import {
    getLayeredMedia, listOrgDesigns, updateLayeredMedia, type DesignSummary,
} from "../../kinema/layeredMediaService";

/*
    Authoring a postcard.

    A mail piece is an Ad like any other, so approval, rebranding and the content lock all
    work unchanged. What it carries is different: two sides of artwork as HTML, and where its
    QR code points. The printer renders that HTML per recipient, which is the only reason a
    card can carry someone's name or a link that belongs to them alone.

    `{{first_name}}` and friends are substituted at print time. `{{code}}` in the QR
    destination is replaced per piece, so a scan identifies one person rather than telling us
    that somebody, somewhere, scanned something.
*/

const SIZES = [
    { value: "4x6", label: "4x6 postcard" },
    { value: "6x9", label: "6x9 postcard" },
    { value: "6x11", label: "6x11 postcard" },
];

interface DirectMailFormProps {
    ad: { id: number; name?: string | null };
    /**
     * Open this side in Kinema.
     *
     * Rachel: "I cannot actually edit it like I can with other image content in Kinema."
     * Picking a finished design or dropping a photo covers the common case; this is for the
     * card somebody actually wants to lay out, and it is the same editor every other piece
     * of content uses.
     */
    onEditSide?: (side: "front" | "back") => void;
    /** CreateEditorModal owns the content lock — see requestEdit there. */
    requestEdit: (run: (force: boolean) => void | Promise<void>) => void;
    /** Which organization's Kinema designs can be imported. */
    organizationId?: number | null;
}

type Fields = {
    size: string;
    front_html: string;
    back_html: string;
    qr_destination: string;
    /** Null until somebody moves the code. Null means the platform default, not "no code". */
    qr_left: number | null;
    qr_bottom: number | null;
    qr_width: number | null;
};

const EMPTY: Fields = {
    size: "4x6", front_html: "", back_html: "", qr_destination: "",
    qr_left: null, qr_bottom: null, qr_width: null,
};

/**
 * Whether a design is the shape of the card, said in the picker rather than afterwards.
 *
 * Rachel imported a 1080x1080 video design onto a 4x6 card and learned it was the wrong shape
 * from a warning that appeared once the artwork was already on the card and already cropped.
 * The composition knows its own size, so the question can be answered before the click.
 *
 * Aspect, not pixels: a design at twice the card's resolution is still the card's shape, and
 * scaling costs nothing. The tolerance is there because a card is 6.25x4.25 and designs get
 * rounded to whole pixels, so an exact match is not something to hold out for.
 *
 * Null when the design's size is unknown, which is not the same as "does not fit" and must
 * not be shown as a warning.
 */
function shapeNote(
    design: Pick<DesignSummary, "width" | "height">, size: MailPieceSize,
): "fits" | "crops" | null {
    const { width, height } = design;
    if (!width || !height) return null;

    const card = CANVAS[size];
    if (!card) return null;

    const ratio = (width / height) / (card.width / card.height);
    return Math.abs(ratio - 1) <= 0.02 ? "fits" : "crops";
}

/**
 * The picture's own pixel dimensions.
 *
 * Needed before the artwork is built, because the fill/fit geometry is computed here rather
 * than left to the printer, whose renderer ignores `object-fit` without saying so.
 */
function measureImage(file: File): Promise<{ naturalWidth: number; naturalHeight: number }> {
    // 4x6 at 300dpi: the size a card is, used when the real one cannot be had. Better a
    // card built on an assumption than an upload that never finishes.
    const FALLBACK = { naturalWidth: 1875, naturalHeight: 1275 };

    return new Promise((resolve) => {
        let settled = false;
        const finish = (value: { naturalWidth: number; naturalHeight: number }) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };

        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            finish({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            finish(FALLBACK);
        };
        // Neither handler is guaranteed to fire -- a decoder can stall, and under jsdom
        // neither fires at all -- and an upload hanging on a promise nobody resolves is
        // worse than one that guesses. Short, because the bytes are already in memory: a
        // local blob that has not decoded by now is not going to.
        setTimeout(() => finish(FALLBACK), 750);
        img.src = url;
    });
}

export default function DirectMailForm({ ad, requestEdit, organizationId, onEditSide }: DirectMailFormProps) {
    /*
        Open on arrival. The panel holds the whole card -- both sides, the size, where the QR
        points -- and somebody who opened a mail piece came to look at it. Collapsed, the
        first thing they saw was a button, and the card they came for was one click away with
        nothing on screen to say it was there.
    */
    const [open, setOpen] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [fields, setFields] = useState<Fields>(EMPTY);
    const [designs, setDesigns] = useState<DesignSummary[]>([]);
    const [designId, setDesignId] = useState<number | null>(null);
    const [designSearch, setDesignSearch] = useState("");
    /*
        Landscape designs only, on by default.

        A postcard is wider than it is tall, so a square or vertical design loses its edges.
        Rachel's picker was a row of squares, every one marked "will crop", while the wide
        designs she wanted sat past the row cap where she could not see them.

        A switch rather than a rule: cropping a square on purpose is a legitimate thing to do,
        and the card offers Fit for exactly that.
    */
    const [landscapeOnly, setLandscapeOnly] = useState(true);
    const [importing, setImporting] = useState(false);
    const [warnings, setWarnings] = useState<ConversionWarning[]>([]);
    const [importError, setImportError] = useState<string | null>(null);
    const [fit, setFit] = useState<"fill" | "fit">("fill");
    const [uploading, setUploading] = useState<"front" | "back" | null>(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [dragSide, setDragSide] = useState<"front" | "back" | null>(null);
    /*
        How the card is being looked at.

        `showGuides` off is "show me the card as it arrives"; on is "show me what the printer
        does to it". `zoom` exists because this panel is narrow and a 6x11 at panel width is
        a postage stamp -- at which point no amount of tidying the guides helps, because
        nothing is legible.
    */
    const [showGuides, setShowGuides] = useState(true);
    /**
     * Whether the raw markup is open. Closed by default: it is the source of truth, not the
     * thing this screen is for, and forty lines of `<style>` under the card is what made
     * designing a postcard feel like editing a web page.
     */
    const [showHtml, setShowHtml] = useState(false);
    /**
     * Which sides already have a composition behind them.
     *
     * A side with artwork but no composition cannot be opened in Kinema without starting
     * from a blank canvas -- and saving there would replace what is on the card.
     */
    const [sideMedia, setSideMedia] = useState<{ front: number | null; back: number | null }>(
        { front: null, back: null },
    );
    const [zoom, setZoom] = useState(1);
    /** The school's approved pages, offered as QR destinations. Empty is a normal state. */
    const [approvedUrls, setApprovedUrls] = useState<ApprovedUrl[]>([]);
    /** Why the last autosave was refused. Null when the card on screen is the card stored. */
    const [saveError, setSaveError] = useState<string | null>(null);
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const fontService = useMemo(() => new FontService(), []);
    const mediaService = useMemo(() => new MediaService(), []);
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;

    useEffect(() => {
        if (!open || loaded) return;
        ccService.getAd(ad.id, ["content"]).then((loadedAd) => {
            const content = (loadedAd.content ?? {}) as Record<string, any>;
            setFields({
                size: content.size ?? "4x6",
                front_html: content.front_html ?? "",
                back_html: content.back_html ?? "",
                qr_destination: content.qr_destination ?? "",
                qr_left: content.qr_left ?? null,
                qr_bottom: content.qr_bottom ?? null,
                qr_width: content.qr_width ?? null,
            });
            // Which sides already have a composition behind them. A side with artwork but
            // no composition cannot be opened in Kinema without starting from blank.
            setSideMedia({
                front: content.front_layered_media_id ?? null,
                back: content.back_layered_media_id ?? null,
            });
            setLoaded(true);
        }).catch(() => setLoaded(true));
    }, [open, loaded, ad.id, ccService]);

    /*
        The school's approved pages, loaded when the panel opens.

        Quietly empty on failure, matching the design list above it: the dropdown simply does
        not appear, and the field still takes a URL by hand. "inherited" so a sub-org sees its
        parent's pages, and active only -- a retired page is not somewhere to send a student.
    */
    useEffect(() => {
        if (!open || !organizationId) return;
        const controller = new AbortController();
        new OrganizationService()
            .getApprovedUrls(organizationId, "inherited", true, controller.signal)
            .then(setApprovedUrls)
            .catch(() => setApprovedUrls([]));
        return () => controller.abort();
    }, [open, organizationId]);

    /** Whether the QR link will resolve. Recomputed as it is typed, shown under the field. */
    const qrProblem = useMemo(
        () => qrDestinationProblem(fields.qr_destination), [fields.qr_destination],
    );

    const save = async (force: boolean) => {
        const { front_html, back_html, ...rest } = fieldsRef.current;
        /*
            A failed save says so.

            Every field here autosaves on blur and nothing reported a rejection, so a save the
            server refused looked exactly like a save that worked: the panel stayed as it was and
            the operator carried on editing a card that had not been written. The QR check added
            alongside this is the first rule that refuses a value somebody can legitimately have
            typed, which is what made the silence worth fixing rather than noting.

            Not a rethrow: the content lock in CreateEditorModal drives this and treats a
            rejection as a lock failure, which is a different thing and a different message.
        */
        try {
            await _write(force, { ...rest, front_html, back_html });
            setSaveError(null);
        } catch (err: any) {
            setSaveError(
                err?.message
                    ? `That did not save: ${err.message}`
                    : "That did not save. Check the fields above and try again.",
            );
        }
    };

    const _write = async (force: boolean, all: Fields) => {
        const { front_html, back_html, ...rest } = all;
        await ccService.updateAd(ad.id, {
            /*
                Only the fields this form owns. It used to read the ad back and spread the
                whole content object into the patch, which meant it also sent fields it does
                not author -- `front_layered_media_id` among them -- as whatever they were a
                moment ago.

                The server patch is partial (`exclude_unset`), so a field left out is left
                alone. Sending it as null is therefore not a no-op: it is an instruction to
                clear it, and that is what cleared it. Importing a design seeds the side's
                composition in one round trip while this save takes two (read, then write),
                so the write reliably landed last and put the id back to null. The card then
                had artwork and no composition, and "edit in Kinema" correctly reported that
                the front was not made in Kinema.

                Keeping this to the form's own fields is also what stops the next field added
                to DirectMailContent from being cleared the same way.

                Artwork goes encoded, everything else goes as it is. A WAF rule rejects CSS in
                a request body before it reaches the API, and every real postcard is styled, so
                the plain fields cannot survive the trip from a browser. The server decodes at
                the schema boundary, so nothing downstream knows (see artworkTransport).
            */
            content: {
                ...rest,
                front_html: "",
                back_html: "",
                front_b64: encodeArtwork(front_html),
                back_b64: encodeArtwork(back_html),
            } as any,
            force_edit: force,
        });
    };

    const set = (patch: Partial<Fields>) => {
        const next = { ...fieldsRef.current, ...patch };
        fieldsRef.current = next;
        setFields(next);
    };

    /*
        Where the QR code sits, and moving it.

        Stored as three nullable numbers rather than an object because that is the shape the
        API takes, and null means "wherever the platform puts it" rather than "no code" -- a
        card with no code is one with a blank destination.

        Clamped again on the way in even though the drag already clamps: the size dropdown can
        change the card under a placement that was legal on the old one, and a 6x11 placement
        on a 4x6 is off the card.
    */
    const qrPlacement = useMemo<QrPlacement>(() => {
        const stored = (fields.qr_left !== null && fields.qr_bottom !== null && fields.qr_width !== null)
            ? { left: fields.qr_left, bottom: fields.qr_bottom, width: fields.qr_width }
            : QR_DEFAULT;
        return clampQrPlacement(fields.size as MailPieceSize, stored);
    }, [fields.qr_left, fields.qr_bottom, fields.qr_width, fields.size]);

    const moveQr = (next: QrPlacement) => {
        set({ qr_left: next.left, qr_bottom: next.bottom, qr_width: next.width });
    };

    /*
        Applying a starter template.

        It replaces both sides, so it asks first when there is something to lose. The check is
        deliberately on the artwork alone: size and QR destination are settings that survive a
        change of design, and re-picking them after every template would be its own annoyance.
    */
    const applyTemplate = (id: string) => {
        const template = templateById(id);
        if (!template) return;

        const hasArtwork = Boolean(fields.front_html.trim() || fields.back_html.trim());
        if (hasArtwork && !window.confirm(
            `Replace the front and back with the "${template.name}" template? `
            + "Your current artwork will be lost.",
        )) return;

        const { front, back } = template.build(fields.size as MailPieceSize);
        set({ front_html: front, back_html: back });
        setWarnings([]);
        requestEdit(save);
    };

    /*
        Designs available to import, loaded when the panel opens.

        Quietly empty on failure rather than shouting: not every organization has Kinema
        designs, and a red box on a panel somebody opened to edit HTML would be noise.
    */
    /*
        The designs on offer: the most recent, or whatever the search matches.

        Debounced because it runs on every keystroke, and this organization has thousands of
        designs behind the query.
    */
    useEffect(() => {
        if (!open || !organizationId) return;
        const timer = setTimeout(() => {
            listOrgDesigns(organizationId, designSearch.trim() || undefined,
                           { landscape: landscapeOnly })
                .then(setDesigns)
                .catch(() => setDesigns([]));
        }, designSearch ? 250 : 0);
        return () => clearTimeout(timer);
    }, [open, organizationId, designSearch, landscapeOnly]);

    /*
        Importing a Kinema design onto one side of the card.

        Per side on purpose. The front and back of a postcard are two different designs, and
        an import that filled both would overwrite the one the operator had already finished.
    */
    /*
        Artwork somebody already has, put straight onto a side.

        Most cards are not designed here. A designer sends a finished JPEG and the only thing
        anyone wants is to get it onto the card, which until now meant a trip through Kinema
        to import it back out again.

        The picture's own dimensions are read before the HTML is built, because the geometry
        is worked out by us rather than by the printer -- `object-fit` is ignored by an engine
        that old, silently, and the image would print stretched to the card's proportions.
    */
    const uploadToSide = async (side: "front" | "back", file: File) => {
        if (!file.type.startsWith("image/")) {
            setImportError("That is not an image. Upload a JPEG or a PNG.");
            return;
        }

        const existing = side === "front" ? fields.front_html : fields.back_html;
        if (existing.trim() && !window.confirm(
            `Replace the ${side} with this image? The current artwork will be lost.`,
        )) return;

        setImportError(null);
        setWarnings([]);
        setUploadPct(0);
        setUploading(side);
        try {
            const measured = await measureImage(file);
            const media = await mediaService.uploadMedia(
                file, file.name, { purpose: "mail_piece" }, setUploadPct,
            );

            /*
                `artwork_url`, never `storage_uri`. The presigned one lasts six hours and Lob
                fetches the image when it renders the piece, days later -- so a card built on
                it prints without its picture and nothing anywhere says so.
            */
            const url = media.artwork_url || media.storage_uri;
            if (!url) {
                setImportError("That image uploaded but came back without an address.");
                return;
            }

            set(side === "front"
                ? { front_html: imageCardHtml(url, {
                      size: fields.size as MailPieceSize, fit, side: "front", ...measured }) }
                : { back_html: imageCardHtml(url, {
                      size: fields.size as MailPieceSize, fit, side: "back", ...measured }) });
            requestEdit(save);
        } catch {
            setImportError("That image could not be uploaded. Try again in a moment.");
        } finally {
            setUploading(null);
            setUploadPct(0);
        }
    };

    const importDesign = async (side: "front" | "back") => {
        const id = designId;
        if (!id) return;

        const existing = side === "front" ? fields.front_html : fields.back_html;
        if (existing.trim() && !window.confirm(
            `Replace the ${side} with this design? The current artwork will be lost.`,
        )) return;

        setImporting(true);
        setImportError(null);
        try {
            const media = await getLayeredMedia(id);
            /*
                The design's own fonts travel with the artwork.

                The printer has only the web-safe set installed but will fetch an @font-face
                it is given, so Inter prints as Inter instead of being swapped for Helvetica.
                Fetched here rather than inside the converter so that stays pure, and so a
                font service that is down costs a substitution rather than the whole import.
            */
            const fontFaces = await collectFontFaces(media.data, (family, weight, italic) =>
                fontService.getFontCss(family, weight, italic).catch(() => null));

            // The design's own picture stands in for any video layer, since a card is one
            // frame. The server hands back the ad's cover for video designs, which is the
            // last frame that is not black.
            const still = designs.find((d) => d.id === designId)?.preview_url ?? undefined;
            const { html, warnings: found } = compositionToPostcardHtml(
                media.data,
                { size: fields.size as MailPieceSize, fit, fontFaces, videoStill: still },
            );
            if (!html) {
                setImportError(found[0]?.detail ?? "That design could not be converted.");
                setWarnings([]);
                return;
            }
            set(side === "front" ? { front_html: html } : { back_html: html });
            setWarnings(found);
            requestEdit(save);

            /*
                Keep the design itself on the side, not just the flattened artwork.

                Rachel picked a design, pressed "edit in Kinema" and got a blank canvas: the
                side's composition is created empty, and the import only ever wrote HTML. A
                copy of the design goes onto the side as well, so opening it shows the thing
                she just chose.

                A copy on purpose -- editing this card must not edit the organisation's
                design, which other cards may be using.

                Recorded in `sideMedia` as well as on the server. That state is what decides
                whether opening Kinema warns about a blank canvas, and it is otherwise only
                ever read from the ad at mount -- so without this the warning appeared even
                when the copy had worked perfectly.

                A failure here is reported rather than logged. The artwork is saved and
                printable either way, but the difference is invisible on the card and only
                shows up later as "this was not made in Kinema" on a design that was.
            */
            try {
                const target = await ccService.openMailPieceSide(ad.id, side);
                await updateLayeredMedia(target.id, { data: media.data });
                setSideMedia((prev) => ({ ...prev, [side]: target.id }));
            } catch {
                setImportError(
                    "The design is on the card, but it could not be made editable in Kinema. "
                    + "Try importing it again.",
                );
            }
        } catch {
            setImportError("Could not load that design. Try again in a moment.");
        } finally {
            setImporting(false);
        }
    };

    const label = { fontSize: 11, color: "#6c7585", display: "block", marginBottom: 2 } as const;
    const input = {
        width: "100%", padding: "5px 8px", border: "1px solid #d7dde8", borderRadius: 4,
        fontSize: 12, outline: "none", color: "#2f3440", boxSizing: "border-box" as const,
        fontFamily: "inherit",
    };

    return (
        <div style={{ marginBottom: 8 }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    background: "none", border: "1px solid #d7dde8", borderRadius: 6,
                    padding: "5px 10px", fontSize: 12, color: "#4f596a", cursor: "pointer",
                    width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 6,
                }}
            >
                <span style={{ fontSize: 10, color: "#6c7585" }}>{open ? "v" : ">"}</span>
                Mail Piece
                <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8" }}>
                    {ad.name || `Ad ${ad.id}`}
                </span>
            </button>

            {open && (
                <div style={{
                    border: "1px solid #e1e6ef", borderTop: "none", borderRadius: "0 0 6px 6px",
                    padding: 10, display: "flex", flexDirection: "column", gap: 8, background: "#fff",
                }}>
                    {!loaded ? (
                        <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: 8 }}>
                            Loading...
                        </div>
                    ) : (
                        <>
                            {/* Above everything, and outside the Kinema block it used to
                                live in. An upload can fail on an organization with no
                                designs at all, and an error rendered inside a section that
                                is not on screen is an error nobody sees -- which is the
                                same silent failure this panel has been fixing all week. */}
                            {importError && (
                                <div className="alert alert-danger py-2 px-3 mb-0" role="alert"
                                     style={{ fontSize: 11 }}>
                                    {importError}
                                </div>
                            )}

                            {/* A refused autosave, in the same place as a refused import: the
                                card on screen is not the card that is stored. */}
                            {saveError && (
                                <div className="alert alert-danger py-2 px-3 mb-0" role="alert"
                                     style={{ fontSize: 11 }}>
                                    {saveError}
                                </div>
                            )}

                            <div>
                                <label style={label}>Size</label>
                                <select
                                    value={fields.size}
                                    onChange={(e) => set({ size: e.target.value })}
                                    onBlur={() => requestEdit(save)}
                                    style={input}
                                    aria-label="Mail piece size"
                                >
                                    {SIZES.map((size) => (
                                        <option key={size.value} value={size.value}>{size.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label style={label}>
                                    Start from a template
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        Fills both sides with print-ready HTML you can edit
                                    </span>
                                </label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                    {MAIL_PIECE_TEMPLATES.map((template) => (
                                        <button
                                            key={template.id}
                                            type="button"
                                            onClick={() => applyTemplate(template.id)}
                                            title={template.description}
                                            style={{
                                                flex: "1 1 auto", background: "#f8fafc",
                                                border: "1px solid #d7dde8", borderRadius: 4,
                                                padding: "4px 8px", fontSize: 11, color: "#4f596a",
                                                cursor: "pointer", whiteSpace: "nowrap",
                                            }}
                                        >
                                            {template.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {(designs.length > 0 || organizationId) && (
                                <div>
                                    <label style={label}>
                                        Or use a design from Kinema
                                        <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                            Your fonts and text come across, and stay editable
                                        </span>
                                    </label>

                                    <input
                                        value={designSearch}
                                        onChange={(e) => setDesignSearch(e.target.value)}
                                        placeholder="Search designs by name"
                                        style={{ ...input, marginBottom: 4 }}
                                        aria-label="Search designs"
                                    />

                                    {/* The shape filter sits with the search because it is
                                        the same gesture: narrowing thousands of designs to
                                        the ones worth looking at. */}
                                    <label style={{
                                        display: "flex", alignItems: "center", gap: 5,
                                        fontSize: 10, color: "#6c7585", marginBottom: 4,
                                        cursor: "pointer",
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={landscapeOnly}
                                            onChange={(e) => setLandscapeOnly(e.target.checked)}
                                        />
                                        Wide designs only
                                        <span style={{ color: "#94a3b8" }}>
                                            a postcard is wider than it is tall, so anything
                                            square or upright loses its edges
                                        </span>
                                    </label>

                                    {/* A row of the designs themselves. Picking artwork is
                                        a visual decision, and a list of names -- half of
                                        which read "<new> Composition" -- is not one. */}
                                    <div style={{
                                        display: "flex", gap: 6, overflowX: "auto",
                                        padding: "2px 0 4px",
                                    }}>
                                        {designs.map((d) => {
                                            const chosen = designId === d.id;
                                            const shape = shapeNote(d, fields.size as MailPieceSize);
                                            return (
                                                <button
                                                    key={d.id}
                                                    type="button"
                                                    onClick={() => setDesignId(chosen ? null : d.id)}
                                                    title={d.label}
                                                    aria-label={`Design ${d.label}`}
                                                    aria-pressed={chosen}
                                                    style={{
                                                        flex: "0 0 auto", width: 74, padding: 3,
                                                        background: chosen ? "#eef2ff" : "#fff",
                                                        border: `1px solid ${chosen ? "#6366f1" : "#d7dde8"}`,
                                                        borderRadius: 4, cursor: "pointer",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {d.preview_url ? (
                                                        <img
                                                            src={d.preview_url}
                                                            alt=""
                                                            style={{
                                                                width: "100%", height: 46,
                                                                objectFit: "cover", borderRadius: 2,
                                                                display: "block", background: "#f1f5f9",
                                                            }}
                                                        />
                                                    ) : (
                                                        // A design saved before previews, or one
                                                        // whose render has not landed yet.
                                                        <div style={{
                                                            height: 46, borderRadius: 2,
                                                            background: "#f1f5f9", color: "#94a3b8",
                                                            fontSize: 9, lineHeight: "46px",
                                                        }}>
                                                            no preview
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        fontSize: 9, color: "#4f596a", marginTop: 2,
                                                        overflow: "hidden", textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}>
                                                        {d.label}
                                                    </div>
                                                    {/* Whether this design is the card's
                                                        shape, said before it is imported.
                                                        It used to be discoverable only from
                                                        a warning afterwards -- by which
                                                        point the artwork was already on the
                                                        card and cropped. */}
                                                    {shape && (
                                                        <div style={{
                                                            fontSize: 8,
                                                            color: shape === "fits" ? "#3f7d58" : "#8a6d1f",
                                                        }}>
                                                            {shape === "fits" ? "card shape" : "will crop"}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                        {designs.length === 0 && (
                                            <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                                {designSearch.trim()
                                                    ? "No designs match that."
                                                    : "No designs in this organization yet."}
                                            </span>
                                        )}
                                    </div>

                                    {/* Only matters when the design is not the card's shape,
                                        and that is exactly when it matters a lot: one of
                                        these loses the edges and the other keeps everything.
                                        Offered up front so nobody has to redesign to find
                                        out. */}
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        marginTop: 4, fontSize: 11, color: "#6c7585",
                                    }}>
                                        <span>If it is not the card&apos;s shape:</span>
                                        {([
                                            ["fill", "Fill (crop edges)"],
                                            ["fit", "Fit (keep it all)"],
                                        ] as const).map(([value, text]) => (
                                            <label key={value} style={{ cursor: "pointer" }}>
                                                <input
                                                    type="radio"
                                                    name={`fit-${ad.id}`}
                                                    checked={fit === value}
                                                    onChange={() => setFit(value)}
                                                    style={{ marginRight: 3 }}
                                                />
                                                {text}
                                            </label>
                                        ))}
                                    </div>


                                    {/* What did not survive the trip. Said here rather than
                                        discovered in the post a week later. */}
                                    {warnings.length > 0 && (
                                        <ul style={{
                                            margin: "6px 0 0", padding: "6px 8px 6px 22px",
                                            background: "#fffbeb", border: "1px solid #fcd34d",
                                            borderRadius: 4, fontSize: 11, color: "#78350f",
                                        }}>
                                            {warnings.map((w) => (
                                                <li key={w.what} style={{ marginBottom: 2 }}>
                                                    <strong>{w.what}.</strong> {w.detail}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {/*
                                Both sides at once, side by side, the way a card is held.
                                Stacked, the back sat below the fold and "Use as front" /
                                "Use as back" were the only clue there were two sides at all.
                                A postcard is one object with two faces and the panel should
                                look like that.
                            */}
                            <div>
                                <div style={{
                                    display: "flex", alignItems: "baseline",
                                    justifyContent: "space-between", gap: 8,
                                }}>
                                    <label style={label}>
                                        The card
                                        <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                            Your browser, not the printer. Test send to be sure.
                                        </span>
                                    </label>

                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        fontSize: 10, color: "#6c7585", whiteSpace: "nowrap",
                                    }}>
                                        <label style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={showGuides}
                                                onChange={(e) => setShowGuides(e.target.checked)}
                                                style={{ marginRight: 3 }}
                                                aria-label="Show print guides"
                                            />
                                            guides
                                        </label>

                                        {/* Bigger, because a 6x11 at panel width is a postage
                                            stamp and nothing on it can be read. */}
                                        <input
                                            type="range"
                                            min={1} max={3} step={0.25}
                                            value={zoom}
                                            onChange={(e) => setZoom(Number(e.target.value))}
                                            style={{ width: 70 }}
                                            aria-label="Preview size"
                                            title="Preview size"
                                        />
                                    </div>
                                </div>
                                {/* At 1x the two sides sit together the way a card is held.
                                    Zoomed in they stack, because two enlarged cards side by
                                    side in a narrow panel is two half-cards. */}
                                <div style={{
                                    display: "flex", gap: 8, alignItems: "flex-start",
                                    flexDirection: zoom > 1.5 ? "column" : "row",
                                    width: `${zoom * 100}%`,
                                    overflowX: zoom > 1 ? "auto" : "visible",
                                }}>
                                    {([
                                        ["front", fields.front_html],
                                        ["back", fields.back_html],
                                    ] as const).map(([whichSide, html]) => (
                                        <div key={whichSide} style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                display: "flex", alignItems: "center",
                                                justifyContent: "space-between",
                                                fontSize: 10, color: "#6c7585", marginBottom: 2,
                                            }}>
                                                <span style={{ textTransform: "capitalize", fontWeight: 600 }}>
                                                    {whichSide}
                                                </span>
                                                <span style={{ display: "flex", gap: 10 }}>
                                                {/*
                                                    Above the card, not under it.

                                                    Rachel: "It looks like I have to click
                                                    'drop an image or choose one' to upload an
                                                    image, which is below the visual... it was
                                                    hard to find." Putting artwork on a side is
                                                    the first thing most people do here, and it
                                                    was the last thing on screen.
                                                */}
                                                <label style={{
                                                    fontSize: 10, color: "#6366f1", cursor: "pointer",
                                                    fontWeight: 600,
                                                }}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: "none" }}
                                                        aria-label={`Upload ${whichSide} image`}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) uploadToSide(whichSide, file);
                                                            e.target.value = "";
                                                        }}
                                                    />
                                                    upload an image
                                                </label>
                                                {/* Applies the selected design to this side.
                                                    On the side it affects, rather than in a
                                                    row of buttons underneath that had to
                                                    name which side each one meant. */}
                                                <button
                                                    type="button"
                                                    disabled={!designId || importing}
                                                    onClick={() => importDesign(whichSide)}
                                                    title={designId
                                                        ? `Put the selected design on the ${whichSide}`
                                                        : "Pick a design first"}
                                                    style={{
                                                        background: "none", border: "none", padding: 0,
                                                        fontSize: 10, cursor: designId ? "pointer" : "default",
                                                        color: designId ? "#6366f1" : "#b8c0cc",
                                                    }}
                                                >
                                                    {importing ? "..." : "use design"}
                                                </button>
                                                {onEditSide && (() => {
                                                    /*
                                                        The control says which of the two
                                                        things it is about to do.

                                                        A side whose artwork came from a
                                                        template, an upload or hand-written
                                                        HTML has no composition to open, so
                                                        Kinema starts blank and saving there
                                                        replaces what is on the card. That
                                                        used to be discoverable only by
                                                        clicking and reading a browser
                                                        dialog -- the screen looked identical
                                                        either way, so there was no way to
                                                        know beforehand and no way to learn
                                                        except by being warned.

                                                        Naming it "rebuild" is the whole
                                                        point: the confirm that follows now
                                                        confirms something already on screen
                                                        instead of springing it.
                                                    */
                                                    const hasArt = (whichSide === "front"
                                                        ? fields.front_html
                                                        : fields.back_html).trim();
                                                    const hasComp = !!sideMedia[whichSide];
                                                    const blank = !!hasArt && !hasComp;
                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (blank && !window.confirm(
                                                                    `This ${whichSide} was not made in Kinema, so it opens as a blank canvas. `
                                                                    + `Saving there will replace the artwork that is on the card now. Continue?`,
                                                                )) return;
                                                                onEditSide(whichSide);
                                                            }}
                                                            title={blank
                                                                ? `This ${whichSide} is artwork without a Kinema design behind it. `
                                                                  + `Kinema opens blank, and saving there replaces what prints.`
                                                                : `Lay this side out in Kinema`}
                                                            style={{
                                                                background: "none", border: "none",
                                                                padding: 0, fontSize: 10,
                                                                cursor: "pointer", fontWeight: 600,
                                                                color: blank ? "#8a6d1f" : "#6366f1",
                                                            }}
                                                        >
                                                            {blank ? "rebuild in Kinema" : "edit in Kinema"}
                                                        </button>
                                                    );
                                                })()}
                                                </span>
                                            </div>
                                            {/* Drop a picture straight onto the side it
                                                belongs on. The whole gesture is "this
                                                artwork, that face of the card", so the
                                                target is the card rather than a button
                                                somewhere else that names a side. */}
                                            <div
                                                onDragOver={(e) => { e.preventDefault(); setDragSide(whichSide); }}
                                                onDragLeave={() => setDragSide(null)}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setDragSide(null);
                                                    const file = e.dataTransfer.files?.[0];
                                                    if (file) uploadToSide(whichSide, file);
                                                }}
                                                style={{
                                                    position: "relative", borderRadius: 4,
                                                    outline: dragSide === whichSide
                                                        ? "2px dashed #6366f1" : "none",
                                                    outlineOffset: 2,
                                                }}
                                            >
                                                <MailPiecePreview
                                                    size={fields.size as MailPieceSize}
                                                    html={html}
                                                    side={whichSide}
                                                    showGuides={showGuides}
                                                    qrPlacement={qrPlacement}
                                                    /* Only the back carries a code, and only
                                                       when there is somewhere for it to point.
                                                       Handing the front a change handler would
                                                       put draggable handles on a side that
                                                       never prints one. */
                                                    onQrPlacementChange={
                                                        whichSide === "back" && fields.qr_destination.trim()
                                                            ? moveQr : undefined
                                                    }
                                                />

                                                {/* The card is still a drop target; this
                                                    only says so, and only while nothing is
                                                    on the side yet. */}
                                                {!html.trim() && uploading !== whichSide && (
                                                    <div style={{
                                                        position: "absolute", left: 0, right: 0,
                                                        bottom: 8, textAlign: "center",
                                                        fontSize: 10, color: "#94a3b8",
                                                        pointerEvents: "none",
                                                    }}>
                                                        or drop an image here
                                                    </div>
                                                )}

                                                {uploading === whichSide && (
                                                    <div style={{
                                                        position: "absolute", inset: 0,
                                                        background: "rgba(255,255,255,0.85)",
                                                        display: "grid", placeItems: "center",
                                                        fontSize: 11, color: "#4f596a",
                                                    }}>
                                                        Uploading {uploadPct}%
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/*
                                The markup, folded away.

                                It used to sit open under the card, so the first thing below
                                the artwork was forty lines of `<style>`. That is the source
                                of truth and it has to stay reachable -- a template can be
                                tuned by hand and somebody occasionally pastes a card in from
                                elsewhere -- but it is not what this screen is for. Designing
                                happens above: pick a size, drop in a picture or a design,
                                look at the card.
                            */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setShowHtml((open) => !open)}
                                    aria-expanded={showHtml}
                                    style={{
                                        background: "none", border: "none", padding: 0,
                                        color: "#2563eb", fontSize: 12, cursor: "pointer",
                                    }}
                                >
                                    {showHtml ? "Hide the HTML" : "Edit the HTML"}
                                    <span style={{ color: "#94a3b8", marginLeft: 6 }}>
                                        {showHtml
                                            ? ""
                                            : "only if you want to change the card by hand"}
                                    </span>
                                </button>
                            </div>

                            {showHtml && (
                                <>
                                    <div>
                                        <label style={label}>
                                            Front
                                            <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                                HTML. {"{{first_name}}"} is filled in per person when it prints.
                                            </span>
                                        </label>
                                        <AutoGrowTextarea
                                            value={fields.front_html}
                                            onChange={(e) => set({ front_html: e.target.value })}
                                            onBlur={() => requestEdit(save)}
                                            rows={4}
                                            style={input}
                                            aria-label="Mail piece front"
                                        />
                                    </div>

                                    <div>
                                        <label style={label}>Back</label>
                                        <AutoGrowTextarea
                                            value={fields.back_html}
                                            onChange={(e) => set({ back_html: e.target.value })}
                                            onBlur={() => requestEdit(save)}
                                            rows={4}
                                            style={input}
                                            aria-label="Mail piece back"
                                        />
                                    </div>
                                </>
                            )}

                            <div>
                                <label style={label}>
                                    QR code link
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        Where a scan sends the person. Blank prints no code.
                                    </span>
                                </label>

                                {/*
                                    The school's own approved pages, offered before the empty box.

                                    This field used to start blank with an example URL greyed out
                                    behind it, and the example was a shape to copy rather than a
                                    page to visit -- so what got typed was a plausible-looking URL
                                    that had never existed. The approved list is the same one every
                                    other ad type picks a link from, and every entry in it resolves.

                                    Absent rather than empty when the organization has no approved
                                    URLs, or has not been passed: an empty dropdown reads as "there
                                    are no pages", which is a different and more alarming claim than
                                    "nobody has approved any yet".
                                */}
                                {approvedUrls.length > 0 && (
                                    <select
                                        value={approvedUrls.some((u) => u.url === fields.qr_destination)
                                            ? fields.qr_destination : ""}
                                        onChange={(e) => {
                                            if (!e.target.value) return;
                                            set({ qr_destination: e.target.value });
                                            requestEdit(save);
                                        }}
                                        style={{ ...input, marginBottom: 4 }}
                                        aria-label="Pick an approved page"
                                    >
                                        <option value="">Pick one of this school&apos;s approved pages</option>
                                        {approvedUrls.map((approved) => (
                                            <option key={approved.id} value={approved.url}>
                                                {approved.title || approved.url}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                <input
                                    value={fields.qr_destination}
                                    onChange={(e) => set({ qr_destination: e.target.value })}
                                    onBlur={() => requestEdit(save)}
                                    placeholder="https://apply.example.edu/visit"
                                    style={input}
                                    aria-label="QR code link"
                                />

                                {/*
                                    Said here, where it can still be fixed.

                                    The same rule is enforced on the server, so a bad link is
                                    refused on save either way -- but a 422 from a field that
                                    autosaves on blur is a worse way to learn it.
                                */}
                                {qrProblem && (
                                    <div
                                        role={qrProblem.severity === "error" ? "alert" : undefined}
                                        style={{
                                            fontSize: 11, marginTop: 4,
                                            color: qrProblem.severity === "error" ? "#b91c1c" : "#b45309",
                                        }}
                                    >
                                        {qrProblem.message}
                                    </div>
                                )}

                                {/*
                                    The correction to what this field used to claim.

                                    It said `{{code}}` was what made a scan traceable to a person,
                                    and told you to add it. It is not: Lob generates and hosts the
                                    code, counts the scan itself and reports it against the
                                    individual mailpiece, so attribution works with a plain shared
                                    URL. The token is only for a destination that wants a source
                                    parameter of its own.
                                */}
                                {!qrProblem && (
                                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
                                        Scans are counted per card automatically. Add
                                        {" "}<code>?src={"{{code}}"}</code> only if the school&apos;s page
                                        wants a per-card source.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
